import React, { useContext, useEffect, useRef, useState } from "react";
import {
  CaptchaConfig,
  PointerHistory,
  Prefix,
  SolvedChallenge,
  Timings,
  VisualSolution,
  VisualSolution1D,
  VisualSolution2D,
} from "../models/Types";
import ProgressIndicator from "./ProgressIndicator";
import { Challenge1D } from "./challenges/Challenge1D";
import Challenge2D from "./challenges/Challenge2D";
import API from "../api/captcha";
import ErrorView from "./Error";
import Retry from "./Retry";
import ChallengeVI from "./challenges/ChallengeVisuallyImpaired";
import { useTranslation } from "react-i18next";
import { PowContext } from "./PowWrapper";
import pako from "pako";
// noinspection TypeScriptCheckImport
import { Buffer } from "buffer";
import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import {
  isDarkModeEnabled,
  maybeMax,
  maybeMin,
  maybeSubtract,
  getPurpose,
  getHvToken,
} from "./Util";

export default function ProtonCaptcha(props: CaptchaConfig) {
  const protonCaptchaRef = useRef<any>(null);
  const canvasContainerRef = useRef<any>(null);

  const [verified, setVerified] = useState(false);
  const [visualSolution, setVisualSolution] = useState<VisualSolution | null>(
    null,
  );
  const [allowedChallenges, setAllowedChallenges] = useState<Array<string>>([]);
  const [token, setToken] = useState("");
  const [failed, setFailed] = useState(false);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState("");
  const [showRetryOnError, setShowRetryOnError] = useState(true);
  const mouseButtonsRef = useRef<number>(0);
  const pointerHistoryRef = useRef<PointerHistory>({
    x: [],
    y: [],
    buttons: [],
    elapsedMs: [],
  });

  const [nAnswersSolvedInBackground, setNAnswersSolvedInBackground] =
    useState<number>(0);
  const {
    answers,
    challenges,
    setChallenges,
    setNLeadingZerosRequired,
    resetPow,
    slowWorker,
  } = useContext(PowContext);
  const powStartMs = useRef<number | null>(null);
  const powEndMs = useRef<number | null>(null);
  const bgLoadStartMs = useRef<number | null>(null);
  const bgLoadEndMs = useRef<number | null>(null);
  const pieceLoadStartMs = useRef<number | null>(null);
  const pieceLoadEndMs = useRef<number | null>(null);
  const visualSolutionAvailableMs = useRef<number | null>(null);

  const nChallenges = challenges.length;
  const nAnswers = answers.length;
  const visualSolutionAvailable = visualSolution !== null;
  const powStarted = nChallenges >= 0 && nAnswers <= nChallenges;
  const powEnded = nChallenges >= 0 && nAnswers == nChallenges;

  const { t } = useTranslation();
  const ld = new LanguageDetector(i18next.services);
  const langs: string | string[] | undefined = ld.detect();
  const supportedLanguages: string =
    (typeof langs === "string" && langs) ||
    (Array.isArray(langs) && langs.join(",")) ||
    "";
  const displayedLang = t("LangCode");

  function initialiseApplication(): void {
    resetPow();

    // fixme: would the Origin header be better suited to this task?
    // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Origin
    const parentURL =
      window.location != window.parent.location
        ? document.referrer
        : document.location.href;

    try {
      API.initialise(
        props.apiBaseURL,
        props.challengeType,
        parentURL,
        props.contestId.current,
        displayedLang,
        supportedLanguages,
        getPurpose(),
        getHvToken(),
      ).then((data) => {
        if (data.forbidden) {
          reset();
          return;
        }

        if (data.error) {
          setError(data.error);
          setShowRetryOnError(false);
        } else {
          setShowRetryOnError(true);
        }

        setToken(data.token);
        setChallenges(data.challenges);
        setAllowedChallenges(data.allowedChallenges);
        setNLeadingZerosRequired(data.nLeadingZerosRequired);
        if (data.contestId) {
          props.contestId.current = data.contestId;
        }
      });
    } catch (err) {
      console.log(err);
      setError("Problem Initialising Application");
    }
  }

  useEffect(() => {
    if (!failed) {
      initialiseApplication();
    }
  }, [protonCaptchaRef, failed]);

  async function validateSolution(solvedChallenge: SolvedChallenge) {
    try {
      const nAnswers = solvedChallenge.answers?.length || 0;
      const incompletePow = nAnswers < nChallenges;
      const verifyResponse = await API.validate(
        props.apiBaseURL,
        token,
        solvedChallenge,
        props.contestId.current,
        getPurpose(),
      );
      if (verified) {
        // Calls to `API.validate()` may be concurrent
        // Thus by the time `await` yields control back, we may have been already verified
        // (fixme: untested codepath, I'm not sure `verified` will really be up-to-date, due to react semantics)
        return;
      }
      if (verifyResponse.ok) {
        // success, end of flow
        setVerified(true);
        resetPow();
        props.successCallback(token);
      } else if (incompletePow) {
        // mercy request rejected, keep going...
      } else {
        // challenge pow went till the end, but the answers are wrong
        setFailed(true);
        setExpired(verifyResponse.status === 404);
      }
    } catch (err) {
      console.log(err);
      setError("Problem Verifying Response");
    }
  }

  function retry(): void {
    setToken("");
    setError("");
    setChallenges([]);
    setFailed(false);
    setExpired(false);
    setVisualSolution(null);
    setNAnswersSolvedInBackground(0);
    props.setViChallengeInProgressCallback(false);
    powStartMs.current = null;
    powEndMs.current = null;
    bgLoadStartMs.current = null;
    bgLoadEndMs.current = null;
    pieceLoadStartMs.current = null;
    pieceLoadEndMs.current = null;
    visualSolutionAvailableMs.current = null;
  }

  function reset(): void {
    retry();
    props.tryAnotherChallengeCallback();
  }

  function recordPointerHistory(event: React.PointerEvent) {
    const canvasBoundingBox = (canvasContainerRef.current &&
      canvasContainerRef.current.getBoundingClientRect()) || { x: 0, y: 0 };
    const x = Math.round(event.clientX - canvasBoundingBox.x);
    const y = Math.round(event.clientY - canvasBoundingBox.y);
    const pointerHistory = pointerHistoryRef.current;
    pointerHistory.x.push(x);
    pointerHistory.y.push(y);
    pointerHistory.buttons.push(mouseButtonsRef.current);
    pointerHistory.elapsedMs.push(performance.now());
  }

  function onPointerUpDown(event: React.PointerEvent) {
    mouseButtonsRef.current = event.buttons;
    recordPointerHistory(event);
  }

  function onPointerMove(event: React.PointerEvent) {
    recordPointerHistory(event);
  }

  function encodeRle(array: Array<number>): Array<number> {
    // scheme: concatenation of blocks in one of the three forms below:
    //   [-n, x1, x2, ..., xn] -> heterogenous numbers
    //   [n, x] -> identical numbers, n times the number x
    //   [0] -> denotes the end of overall array
    // example: [3, 1, -2, 2, 3, 5, 4, 0] is the rle-encoded version of [1, 1, 1, 2, 3, 4, 4, 4, 4, 4]
    if (array.length === 0) {
      return [0];
    }
    let last = array[0];
    let identicalRunLength = 1;
    let nonidenticalRun = [last];
    const output = [];
    for (const x of array.slice(1)) {
      if (x === last) {
        if (nonidenticalRun.length > 1 || nonidenticalRun.length >= 32767) {
          nonidenticalRun.pop();
          output.push(-nonidenticalRun.length);
          output.push(...nonidenticalRun);
          nonidenticalRun = [];
        }
        identicalRunLength++;
      } else {
        if (identicalRunLength > 1 || identicalRunLength >= 32767) {
          output.push(identicalRunLength);
          output.push(last);
          identicalRunLength = 1;
          nonidenticalRun = [x];
        } else {
          nonidenticalRun.push(x);
        }
      }
      last = x;
    }

    if (nonidenticalRun.length > 1) {
      output.push(-nonidenticalRun.length);
      output.push(...nonidenticalRun);
    } else {
      output.push(identicalRunLength);
      output.push(last);
    }

    output.push(0);
    return output;
  }

  function obfuscatePointerHistory(pointerHistory: PointerHistory): string {
    // The point here is twofold:
    //   1. obfuscate pointer history data such that a lazy attacker does not reverse-engineer this field and is
    //      inclined to submit a bogus value, possibly reusing one from a previous capture. This is a honeypot:
    //      a reuse is suspicious.
    //   2. slightly compress the data such that network transfer and db storage is not too high.
    const { x, y, buttons, elapsedMs } = pointerHistory;
    // Our RLE encoding has an end marker (0 byte) thus we can flatten multiple arrays in a single one.
    const flatRle = [
      ...encodeRle(x),
      ...encodeRle(y),
      ...encodeRle(buttons),
      ...encodeRle(elapsedMs),
    ];
    // We make the hypothesis that values do not exceed 65535 (or they will be wrapped).
    // For x, y, buttons, this should be always true (unless our user has a REALLY big screen!)
    // For elapsedMs, this means > 1min interaction will wrap over, which is unlikely.
    const buffer16 = new Int16Array(flatRle);
    const buffer8 = new Uint8Array(
      buffer16.buffer,
      buffer16.byteOffset,
      buffer16.byteLength,
    );
    // We compress the flat array
    const compressed = pako.deflate(buffer8);
    // Obfuscate the magic numbers such that `file` does not give away the format too easily
    compressed[0] ^= 0b10010110;
    compressed[1] ^= 0b10110101;
    const b64 = new Buffer(compressed).toString("base64");
    return b64;
  }

  function getTimings(): Timings {
    const challengeLoadEndMs = maybeMax(
      bgLoadEndMs.current,
      pieceLoadEndMs.current,
    );
    const challengeLoadStartMs = maybeMin(
      bgLoadStartMs.current,
      pieceLoadStartMs.current,
    );
    const bgLoadElapsedMs = maybeSubtract(
      bgLoadEndMs.current,
      bgLoadStartMs.current,
    );
    const pieceLoadElapsedMs = maybeSubtract(
      pieceLoadEndMs.current,
      pieceLoadStartMs.current,
    );
    const challengeLoadElapsedMs = maybeSubtract(
      challengeLoadEndMs,
      challengeLoadStartMs,
    );
    const solveChallengeMs = maybeSubtract(
      visualSolutionAvailableMs.current,
      challengeLoadEndMs,
    );
    const powEndOrNowMs = maybeMax(powEndMs.current, performance.now());
    const powElapsedMs = maybeSubtract(powEndOrNowMs, powStartMs.current);
    return {
      ...(pieceLoadElapsedMs !== null ? { pieceLoadElapsedMs } : {}),
      ...(bgLoadElapsedMs !== null ? { bgLoadElapsedMs } : {}),
      ...(challengeLoadElapsedMs !== null ? { challengeLoadElapsedMs } : {}),
      ...(solveChallengeMs !== null ? { solveChallengeMs } : {}),
      ...(powElapsedMs !== null ? { powElapsedMs } : {}),
    };
  }

  function submitPowAnswersAndVisualSolution(
    answers: Array<Prefix>,
    visualSolution: VisualSolution,
  ) {
    const encodedPointerHistory = obfuscatePointerHistory(
      pointerHistoryRef.current,
    );

    const backgroundData = {
      answers,
      clientData: encodedPointerHistory,
      ...getTimings(),
    };
    let solvedChallenge: SolvedChallenge;
    if (props.challengeType == "1D") {
      const solution1D = visualSolution as VisualSolution1D;
      solvedChallenge = {
        ...solution1D,
        ...backgroundData,
      };
    } else if (props.challengeType == "2D") {
      const solution2D = visualSolution as VisualSolution2D;
      solvedChallenge = {
        ...solution2D,
        ...backgroundData,
      };
    } else if (props.challengeType == "VI") {
      solvedChallenge = {
        visuallyImpaired: true,
        ...backgroundData,
      };
    } else {
      console.log(`unknown challenge type: ${props.challengeType}`);
      return;
    }
    validateSolution(solvedChallenge);
  }

  function submitVisualSolution(visualSolution: SolvedChallenge) {
    setVisualSolution(visualSolution);
    setNAnswersSolvedInBackground(nAnswers);
    if (
      Object.prototype.hasOwnProperty.call(visualSolution, "visuallyImpaired")
    ) {
      props.setViChallengeInProgressCallback(true);
    }
  }

  function monitorCompletion() {
    if (powStarted && powStartMs.current === null) {
      powStartMs.current = performance.now();
    }
    if (powEnded && powEndMs.current === null) {
      powEndMs.current = performance.now();
    }
    if (visualSolutionAvailable && visualSolutionAvailableMs.current === null) {
      visualSolutionAvailableMs.current = performance.now();
    }
    const canAskForMercy = slowWorker && props.challengeType !== "VI";
    if ((powEnded || canAskForMercy) && visualSolution !== null) {
      submitPowAnswersAndVisualSolution(answers, visualSolution);
    }
  }



  useEffect(monitorCompletion, [
    powStarted,
    powEnded,
    visualSolutionAvailable,
    slowWorker,
  ]);

  return (
    <div
      onPointerMove={onPointerMove}
      onPointerDown={onPointerUpDown}
      onPointerUp={onPointerUpDown}
      ref={protonCaptchaRef}
    >
      {error !== "" && (
        <ErrorView
          message={error}
          showIcon={props.challengeType != "VI"}
          showRetry={showRetryOnError}
          retryCallback={reset}
          apiBaseURL={props.apiBaseURL}
        />
      )}
      {!error && !verified && !failed && token !== "" && (
        <div>
          {props.promptText && (
            <>
              <p className={"helpText no-top-margin"}>{props.promptText}</p>
              <p className="tiny-text">To succeed at this task, ensure the piece is 18 pixels from the target</p>
              <p className="sr-only">{t("VisuallyImpairedHelp.text")}</p>
            </>
          )}

          <div ref={canvasContainerRef}>
            {props.challengeType === "1D" && token && (
              <Challenge1D
                width={props.width}
                height={props.height}
                bgImageURL={
                  `${props.apiBaseURL}/bg?token=${token}` +
                  (isDarkModeEnabled() ? "&dark=true" : "")
                }
                submitVisualSolutionCallback={submitVisualSolution}
                onBgLoadStart={() =>
                  (bgLoadStartMs.current = performance.now())
                }
                onBgLoadEnd={() => (bgLoadEndMs.current = performance.now())}
              />
            )}

            {props.challengeType === "2D" && token && (
              <Challenge2D
                width={props.width}
                height={props.height}
                bgImageURL={`${props.apiBaseURL}/bg?token=${token}`}
                puzzleImageURL={`${props.apiBaseURL}/puzzle?token=${token}`}
                submitVisualSolutionCallback={submitVisualSolution}
                onBgLoadStart={() =>
                  (bgLoadStartMs.current = performance.now())
                }
                onBgLoadEnd={() => (bgLoadEndMs.current = performance.now())}
                onPieceLoadStart={() =>
                  (pieceLoadStartMs.current = performance.now())
                }
                onPieceLoadEnd={() =>
                  (pieceLoadEndMs.current = performance.now())
                }
              />
            )}

            {props.challengeType === "VI" && !visualSolutionAvailable && (
              <ChallengeVI
                submitVisualSolutionCallback={submitVisualSolution}
              />
            )}
          </div>
        </div>
      )}

      {!error &&
        !verified &&
        !failed &&
        visualSolutionAvailable &&
        !powEnded && (
          <ProgressIndicator
            nAnswersSolvedInBackground={nAnswersSolvedInBackground}
          />
        )}

      {failed && props.challengeType === "VI" && (
        <VerificationUnsuccessful
          resetCallback={props.tryAnotherChallengeCallback}
        />
      )}

      {failed && props.challengeType !== "VI" && (
        <Retry
          apiBaseURL={props.apiBaseURL}
          expired={expired}
          allowedChallenges={allowedChallenges}
          tryAnotherChallengeCallback={props.tryAnotherChallengeCallback}
          retryCallback={retry}
        />
      )}
    </div>
  );
}

function VerificationUnsuccessful(resetCallback: any) {
  const { t } = useTranslation();

  return (
    <div>
      <p>{t("VerificationUnsuccessful.text")}</p>
      <a href="#" onClick={resetCallback}>
        {t("Retry.text")}
      </a>
    </div>
  );
}
